import { z } from "zod";
import { BaseAdapter } from "../base.js";
import type { AdapterContext } from "../base.js";
import type { Url } from "../../data-model/primitives.js";
import type { NovelMetadata, ChapterRef, Chapter } from "../../data-model/novel.js";
import { unsafeBrandAdapterId, unsafeBrandUrl } from "../../data-model/primitives.js";
import { ParseError } from "../../core/errors.js";
import {
  parseNovelPage,
  parseChapterPage,
  parseTocPage1,
  parseTocJsonPage,
  extractBookId,
  extractLastPageNumber,
} from "./parser.js";
import { METRUYENCHU_SELECTORS } from "./selectors.js";
import { paginateToc, type TocWalkPage } from "../shared/paginate.js";

const tocJsonEnvelopeSchema = z.object({ data: z.string() });

export class MeTruyenChuComVnAdapter extends BaseAdapter {
  readonly id = unsafeBrandAdapterId("metruyenchu-com-vn");
  readonly displayName = "MeTruyenChu.com.vn";
  readonly hostnames = ["metruyenchu.com.vn", "www.metruyenchu.com.vn"];

  async fetchMetadata(url: Url, ctx: AdapterContext): Promise<NovelMetadata> {
    const $ = await this.loadHtml(url, ctx);
    return parseNovelPage($, url);
  }

  async *fetchChapterList(url: Url, ctx: AdapterContext): AsyncIterable<ChapterRef> {
    const $ = await this.loadHtml(url, ctx);
    const bid = extractBookId($);
    if (!bid) {
      throw new ParseError("Missing book id on novel page", { url });
    }
    const lastPage = extractLastPageNumber($);
    if (lastPage === 1 && $(METRUYENCHU_SELECTORS.metadata.pagingPageFn).length > 0) {
      ctx.logger.warn(
        { url },
        "metruyenchu: pagination controls present but last-page parse fell back to 1 — onclick format may have drifted (survey needed)",
      );
    }
    const apiOrigin = new URL(url).origin;

    yield* paginateToc({
      ctx,
      fetchPage: async (page, startIndex): Promise<TocWalkPage | null> => {
        if (page === 1) {
          return { refs: parseTocPage1($, startIndex, url), isLast: lastPage <= 1 };
        }
        const apiUrl = unsafeBrandUrl(`${apiOrigin}/get/listchap/${bid}?page=${page}`);
        const raw = await this.fetchJson(apiUrl, ctx);
        const parsed = tocJsonEnvelopeSchema.safeParse(raw);
        if (!parsed.success) {
          throw new ParseError(`Invalid TOC JSON envelope at ${apiUrl}`, {
            cause: parsed.error,
            url: apiUrl,
          });
        }
        const fragment = parsed.data.data;
        if (!fragment) {
          ctx.logger.warn({ apiUrl, page }, "Empty TOC JSON data; ending walk early");
          return null;
        }
        const refs = parseTocJsonPage(fragment, startIndex, url);
        if (refs.length === 0) {
          ctx.logger.warn({ apiUrl, page }, "Zero anchors in TOC JSON page; ending walk early");
          return null;
        }
        return { refs, isLast: page >= lastPage };
      },
    });
  }

  async fetchChapter(ref: ChapterRef, ctx: AdapterContext): Promise<Chapter> {
    const $ = await this.loadHtml(ref.url, ctx);
    return parseChapterPage($, ref);
  }
}
